import { sommeAuCentime } from '@taki/data-model';
import type { RollupFieldDef } from '@taki/ui';
import type { FicheGenerique } from './app-declaree.js';

/**
 * LE TOTAL LIE D'UNE FICHE, CALCULE PAR L'HOTE (lot du 14/10/2026) : le
 * « Temps saisi » de chaque tache d'un kanban, somme des durees de ses saisies.
 *
 * POURQUOI ICI, ET PAS UNE REQUETE PAR CARTE. L'ecran declare charge DEJA chacun
 * de ses jeux EN ENTIER (`chargerLesJeux`, aucun plafond : c'est ce qui rend les
 * compteurs du menu justes, regle G15). Le jeu lie est donc en memoire, complet :
 * on le GROUPE une fois par fiche parente, et chaque carte lit son chiffre. Une
 * requete d'agregat par carte ferait des dizaines d'allers-retours pour un
 * resultat identique.
 *
 * CE N'EST PAS UN COMPTE DE PAGE (`docs/DONNEES-SDK.md`) : le jeu n'est pas une
 * page, et quand il n'est PAS charge (`lire` rend `undefined`), le calcul n'a
 * pas lieu et la case reste vide, plutot que d'afficher un nombre faux.
 *
 * LES ARCHIVES NE COMPTENT PAS, comme dans les compteurs : en mode archives le
 * jeu en contient, elles sont ecartees ici. Une fiche sans fiche liee n'a pas de
 * total (`null`, case vide) : « 0 min » sur chaque carte neuve serait du bruit.
 *
 * UN MONTANT SE SOMME AU CENTIME (`auCentime`, `sommeAuCentime`) : additionner
 * des flottants fait 0,1 + 0,2 = 0,30000000000000004, et le total d'un projet
 * de cent lignes derive d'un centime. Une duree ou un nombre se somment tels
 * quels.
 */
export function totalLieDepuisJeu(
  total: Pick<RollupFieldDef, 'agg' | 'field' | 'link'>,
  lire: () => readonly FicheGenerique[] | undefined,
  auCentime = false,
): (fiche: FicheGenerique) => number | null {
  let source: readonly FicheGenerique[] | undefined;
  let groupes = new Map<string, number[]>();
  const regrouper = (fiches: readonly FicheGenerique[]): Map<string, number[]> => {
    const parParent = new Map<string, number[]>();
    for (const liee of fiches) {
      if (liee['archivedAt'] != null || liee['deletedAt'] != null) continue;
      const parent = lireChemin(liee, total.link.by);
      if (typeof parent !== 'string' || parent === '') continue;
      const valeur = total.field === undefined ? 1 : lireChemin(liee, total.field);
      const nombre = typeof valeur === 'number' && Number.isFinite(valeur) ? valeur : null;
      const liste = parParent.get(parent) ?? [];
      /* Un `count` compte la fiche ; une somme ou une moyenne ignore une case
         vide, comme l'agregat du serveur. */
      if (total.agg === 'count') liste.push(1);
      else if (nombre !== null) liste.push(nombre);
      parParent.set(parent, liste);
    }
    return parParent;
  };
  return (fiche) => {
    const fiches = lire();
    if (fiches === undefined) return null;
    if (fiches !== source) {
      source = fiches;
      groupes = regrouper(fiches);
    }
    const id = fiche['id'];
    const valeurs = typeof id === 'string' ? groupes.get(id) : undefined;
    if (valeurs === undefined || valeurs.length === 0) return null;
    const somme = auCentime && total.agg !== 'count'
      ? sommeAuCentime(valeurs)
      : valeurs.reduce((cumul, valeur) => cumul + valeur, 0);
    return total.agg === 'avg' ? somme / valeurs.length : somme;
  };
}

/** Un chemin de stockage (`taskId`, `customFields.cout`) lu sur un document. */
function lireChemin(fiche: FicheGenerique, chemin: string): unknown {
  return chemin.split('.').reduce<unknown>(
    (valeur, clef) =>
      valeur !== null && typeof valeur === 'object' ? (valeur as Record<string, unknown>)[clef] : undefined,
    fiche,
  );
}
